import {
  BookingMoveBatchMode,
  REASSIGNMENT_WRITE_OFF_REASON,
  RESCHEDULE_FAILED_REASON,
  ReviewDecision,
  type BookingReviewDetail,
  type ReviewDecisionValue,
} from "@/app/booking-calendar/api/reviewApi"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { ApiError } from "@/lib/api"
import { appointmentWindowLabel } from "./reviewBookingSummary"

const ALL_DECISIONS: readonly ReviewDecisionValue[] = [ReviewDecision.Keep, ReviewDecision.CancelDuplicate]

/**
 * The decision buttons the review dialog offers, in order (AB#809): what the server says the request
 * accepts — Keep alone for a failed batch move — and both when it does not say (an older server).
 * Anything the client cannot name is dropped rather than offered as a button that would be refused.
 */
export function allowedDecisions(detail: BookingReviewDetail | null | undefined): ReviewDecisionValue[] {
  const allowed = detail?.allowedDecisions
  if (!allowed) return [...ALL_DECISIONS]
  return ALL_DECISIONS.filter((decision) => allowed.includes(decision))
}

/** The "Batch / attempted target / failure" lines of a failed batch move, or null when there is none. */
export interface RescheduleFailureLines {
  batch: string
  target: string
  failure: string
}

/** The failed batch move's block, in the site's zone; null for a request without one. */
export function rescheduleFailureLines(
  detail: BookingReviewDetail | null | undefined,
  timeZone: string = SITE_TIME_ZONE,
): RescheduleFailureLines | null {
  const failure = detail?.rescheduleFailure
  if (!failure) return null

  const mode = failure.mode === BookingMoveBatchMode.Automatic ? "Automatic reassignment" : "Manual reschedule"
  const host = failure.targetHostName || failure.targetHostEmail || `Cal user ${failure.targetCalUserId}`
  const when = appointmentWindowLabel(failure.targetStart, null, timeZone) ?? failure.targetStart

  return {
    batch: `${mode} · batch ${failure.batchId.slice(0, 8)}`,
    target: `${host} · ${when}`,
    failure: failure.failureDetail || "No reason was recorded.",
  }
}

/**
 * The review reasons, by AmsBookingReviewReason bit, in the order the queue shows them. A bitmask
 * because one request routinely trips several at once and staff want to see all of them.
 */
export const REVIEW_REASONS = [
  { bit: 1, label: "Duplicate appointment" },
  { bit: 2, label: "Phone used under another name" },
  { bit: 4, label: "Household already received" },
  { bit: 8, label: "Household application in progress" },
  { bit: 16, label: "Address used under another name" },
  // AB#809: a booking-move batch could not move it. Acknowledged with Keep; fixed with the ordinary
  // appointment actions.
  { bit: RESCHEDULE_FAILED_REASON, label: "Reschedule failed" },
  // ams-u3b: the sweep wrote off a reassignment that had already cancelled the Cal booking.
  // Acknowledged with Keep; the household is rebooked or cancelled with the ordinary actions.
  { bit: REASSIGNMENT_WRITE_OFF_REASON, label: "Reassignment written off" },
] as const

/** The labels of the bits set in `value`, from `table`, in the table's order. */
export function decodeFlags(value: number, table: readonly { bit: number; label: string }[]): string[] {
  return table.filter((entry) => (value & entry.bit) !== 0).map((entry) => entry.label)
}

/** The labels of a request's review reasons. */
export function reviewReasonLabels(reasons: number): string[] {
  return decodeFlags(reasons, REVIEW_REASONS)
}

/** The "Attempted move / appointment / reason" lines of a written-off reassignment, or null. */
export interface ReassignmentWriteOffLines {
  target: string
  appointment: string
  writtenOff: string
  reason: string
}

/** A written-off reassignment's block, in the site's zone; null for a request without one. */
export function reassignmentWriteOffLines(
  detail: BookingReviewDetail | null | undefined,
  timeZone: string = SITE_TIME_ZONE,
): ReassignmentWriteOffLines | null {
  const writeOff = detail?.reassignmentWriteOff
  if (!writeOff) return null

  return {
    target: writeOff.targetHostEmail || `Cal user ${writeOff.targetCalUserId}`,
    appointment: appointmentWindowLabel(writeOff.slotStart, null, timeZone) ?? "—",
    writtenOff: appointmentWindowLabel(writeOff.writtenOffAt, null, timeZone) ?? "—",
    reason: writeOff.failureDetail || "No reason was recorded.",
  }
}

/**
 * What the review dialog says when **Reveal** fails. The reveal needs nothing beyond the page's own
 * `appointments:manage` since AB#845, so no failure is put down to a missing permission. A 404 is the
 * server's one answer for a request with no number on file, including one already erased.
 */
export function revealFailureMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "There is no Social Security number on file for this request. It may have been erased."
  }
  return "The Social Security number could not be revealed. Please try again."
}
